/**
 * NewSuiteDialog — the unified create-suite dialog (#936 follow-up).
 *
 * Mockup: `docs/mockups/new-suite-workflow-mode.html`. The dialog is
 * the single entry point for creating a new TestSuite — picks Name,
 * Description, Type (Batch | Workflow), members, and (workflow only)
 * the skip-rest opt-in + per-member step labels, then POSTs the whole
 * shape atomically on Save.
 *
 * Replaces two prior surfaces (#936 PR 1 misstep):
 *   - The duplicated `+ New Workflow` toolbar button that shipped
 *     alongside `+ New Suite` (two entry points where the mockup
 *     specified one).
 *   - The post-creation "Convert to Workflow / Convert to Suite"
 *     button in SuiteDetail. Type is set-once at create; if the
 *     operator picks the wrong type they re-create.
 *
 * UI choices:
 *   - Batch mode: tests render as a checkbox list. Order doesn't matter.
 *   - Workflow mode: tests render as an ordered, drag-reorderable
 *     step list with per-step label inputs + a "+ Add Tests" inline
 *     picker. Reports render in the same order.
 *   - The skip-rest toggle is workflow-only; the description-of-effect
 *     copy matches SuiteDetail's identical toggle (#936 PR 1).
 *
 * Drafting strategy: all state is local to the dialog. The single
 * Save call uses `testSuitesAPI.create()` with the assembled payload;
 * cancel discards everything. No partial saves, no "draft suites" —
 * the suite doesn't exist until Save.
 */
import { useMemo, useState, type CSSProperties } from 'react'
import { GripVertical, Plus, X } from 'lucide-react'
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui'
import { SearchInput } from '@/components/SearchInput'
import { testSuitesAPI } from '@/lib/api'
import type { PlanTestCase, TestSuite, WorkflowMemberMetadata } from '@/types'

type SuiteMode = 'batch' | 'workflow'

export interface NewSuiteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  applicationId: string
  variantId: string
  allCases: PlanTestCase[]
  /** Called after the new suite is created. Receives the created
   * suite so the caller can jump selection to it. */
  onCreated: (suite: TestSuite) => void
}

export function NewSuiteDialog({
  open,
  onOpenChange,
  applicationId,
  variantId,
  allCases,
  onCreated,
}: NewSuiteDialogProps) {
  // ── Form state ───────────────────────────────────────────────────
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [mode, setMode] = useState<SuiteMode>('batch')
  const [skipRest, setSkipRest] = useState(false)
  /** Batch mode: order-independent membership. */
  const [batchSelectedIds, setBatchSelectedIds] = useState<Set<string>>(new Set())
  /** Workflow mode: ordered member ids (order IS the workflow order). */
  const [workflowOrderedIds, setWorkflowOrderedIds] = useState<string[]>([])
  /** Workflow mode: per-member step label, keyed by testCaseId. */
  const [stepLabels, setStepLabels] = useState<Record<string, string>>({})
  /** Workflow mode: in-dialog "+ Add Tests" picker visibility. */
  const [showWorkflowPicker, setShowWorkflowPicker] = useState(false)
  const [workflowPickerSearch, setWorkflowPickerSearch] = useState('')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset everything when the dialog closes. Cancel discards in
  // progress; opening fresh starts from blanks. We reset on close
  // rather than open to avoid a render flicker.
  function reset() {
    setName('')
    setDescription('')
    setMode('batch')
    setSkipRest(false)
    setBatchSelectedIds(new Set())
    setWorkflowOrderedIds([])
    setStepLabels({})
    setShowWorkflowPicker(false)
    setWorkflowPickerSearch('')
    setError(null)
  }

  // ── Mode helpers ─────────────────────────────────────────────────

  /** Workflow-mode candidates for the inline picker — tests not yet
   * added to the workflow. Narrowed by search. */
  const workflowCandidates = useMemo(() => {
    const added = new Set(workflowOrderedIds)
    let pool = allCases.filter((c) => !added.has(c.id))
    if (workflowPickerSearch.trim()) {
      const q = workflowPickerSearch.toLowerCase()
      pool = pool.filter((c) => c.name.toLowerCase().includes(q))
    }
    return pool
  }, [allCases, workflowOrderedIds, workflowPickerSearch])

  const casesById = useMemo(() => {
    const m = new Map<string, PlanTestCase>()
    for (const c of allCases) m.set(c.id, c)
    return m
  }, [allCases])

  // ── DnD wiring (workflow mode) ───────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = workflowOrderedIds.indexOf(String(active.id))
    const newIndex = workflowOrderedIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    setWorkflowOrderedIds(arrayMove(workflowOrderedIds, oldIndex, newIndex))
  }

  // ── Save ─────────────────────────────────────────────────────────

  async function handleSave() {
    if (!name.trim() || saving) return
    setSaving(true)
    setError(null)
    try {
      // Build the payload from local state. Batch and workflow shapes
      // differ — see field-by-field comments below.
      const isWorkflow = mode === 'workflow'
      const testCaseIds = isWorkflow
        ? workflowOrderedIds
        : Array.from(batchSelectedIds)

      // Step labels: only persist entries where the user typed
      // something. Empty labels render as the test name placeholder
      // in the report; storing them would just be noise.
      let workflowMetadata: Record<string, WorkflowMemberMetadata> | undefined
      if (isWorkflow) {
        const trimmedLabels: Record<string, WorkflowMemberMetadata> = {}
        for (const id of workflowOrderedIds) {
          const label = stepLabels[id]?.trim()
          if (label) trimmedLabels[id] = { stepLabel: label }
        }
        workflowMetadata = Object.keys(trimmedLabels).length ? trimmedLabels : undefined
      }

      const created = await testSuitesAPI.create({
        applicationId,
        variantId,
        name: name.trim(),
        description: description.trim() || undefined,
        testCaseIds,
        ciProtected: false,
        ...(isWorkflow
          ? {
              isWorkflow: true,
              onMemberFailure: skipRest ? 'skip-rest' : 'continue',
              ...(workflowMetadata ? { workflowMetadata } : {}),
            }
          : {}),
      })
      onCreated(created)
      reset()
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create suite')
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    reset()
    onOpenChange(false)
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  function toggleBatchMember(id: string) {
    setBatchSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function addWorkflowMember(id: string) {
    setWorkflowOrderedIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }

  function removeWorkflowMember(id: string) {
    setWorkflowOrderedIds((prev) => prev.filter((m) => m !== id))
    setStepLabels((prev) => {
      const { [id]: _drop, ...rest } = prev
      return rest
    })
  }

  // ── Render ───────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>New Test Suite</DialogTitle>
            <Badge
              variant={mode === 'workflow' ? 'default' : 'secondary'}
              className="text-[10px] uppercase tracking-wider"
            >
              {mode === 'workflow' ? 'Workflow' : 'Batch'}
            </Badge>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {error && (
            <div
              className="border bg-destructive/10 text-destructive text-xs px-3 py-2 rounded"
              role="alert"
            >
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="new-suite-name" className="text-sm">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="new-suite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Smoke Tests / Customer Workflow"
              aria-label="Suite name"
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="new-suite-description" className="text-sm">
              Description
            </Label>
            <Textarea
              id="new-suite-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this suite cover?"
              rows={2}
              aria-label="Suite description"
            />
          </div>

          {/* Type — Batch / Workflow radio cards */}
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">Type</legend>
            <div className="space-y-2">
              <TypeOption
                value="batch"
                title="Batch"
                description="A grouping for running tests together; OttoTester may run them in parallel. Reports show members as a flat checklist."
                selected={mode === 'batch'}
                onSelect={() => setMode('batch')}
              />
              <TypeOption
                value="workflow"
                title="Workflow"
                description="A grouping representing a business workflow. Reports show members in your chosen order, each with a label."
                selected={mode === 'workflow'}
                onSelect={() => setMode('workflow')}
              />
            </div>
          </fieldset>

          {/* Workflow-only: skip-rest toggle. Lives ABOVE the Tests
              section so the operator sees the failure-handling choice
              before deciding on order. Copy matches the SuiteDetail
              toggle (one source of truth for this string). */}
          {mode === 'workflow' && (
            <div className="space-y-1.5">
              <Label className="text-sm">Failure handling</Label>
              <label className="flex items-start gap-2 cursor-pointer border rounded-md px-3 py-2 hover:border-primary/40">
                <input
                  type="checkbox"
                  checked={skipRest}
                  onChange={(e) => setSkipRest(e.target.checked)}
                  className="mt-0.5 accent-primary"
                />
                <div>
                  <div className="text-sm">Skip remaining tests if an earlier one fails</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    When enabled, OttoTester runs the workflow's tests in order and skips later tests once any test fails — useful for long-running workflows. Off by default; tests run independently in parallel.
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Members / Tests section. Top border + uppercase label
              mirrors the mockup's section-header style. */}
          <div className="pt-3 border-t">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              {mode === 'workflow' ? 'Tests' : 'Members'}
            </Label>

            {mode === 'batch' ? (
              <BatchMemberPicker
                allCases={allCases}
                selectedIds={batchSelectedIds}
                onToggle={toggleBatchMember}
              />
            ) : (
              <WorkflowStepEditor
                orderedIds={workflowOrderedIds}
                casesById={casesById}
                stepLabels={stepLabels}
                onStepLabelChange={(id, label) =>
                  setStepLabels((prev) => ({ ...prev, [id]: label }))
                }
                onRemove={removeWorkflowMember}
                sensors={sensors}
                onDragEnd={handleDragEnd}
                pickerOpen={showWorkflowPicker}
                onTogglePicker={() => setShowWorkflowPicker((v) => !v)}
                candidates={workflowCandidates}
                pickerSearch={workflowPickerSearch}
                onPickerSearchChange={setWorkflowPickerSearch}
                onAdd={(id) => {
                  addWorkflowMember(id)
                  setWorkflowPickerSearch('')
                }}
              />
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : 'Save Suite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Type radio-card ──────────────────────────────────────────────────

function TypeOption({
  value,
  title,
  description,
  selected,
  onSelect,
}: {
  value: SuiteMode
  title: string
  description: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <label
      className={`flex items-start gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-colors ${
        selected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/40'
      }`}
    >
      <input
        type="radio"
        name="new-suite-type"
        value={value}
        checked={selected}
        onChange={onSelect}
        className="mt-1 accent-primary"
        aria-label={title}
      />
      <div className="flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
      </div>
    </label>
  )
}

// ── Batch members (checkbox list) ────────────────────────────────────

function BatchMemberPicker({
  allCases,
  selectedIds,
  onToggle,
}: {
  allCases: PlanTestCase[]
  selectedIds: Set<string>
  onToggle: (id: string) => void
}) {
  if (allCases.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic mt-2">
        No tests in this variant yet — create some from the Tests page first.
      </p>
    )
  }

  return (
    <>
      <div className="mt-2 border rounded-md max-h-64 overflow-y-auto divide-y">
        {allCases.map((c) => {
          const checked = selectedIds.has(c.id)
          return (
            <label
              key={c.id}
              className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(c.id)}
                className="accent-primary"
              />
              <span className="text-sm flex-1 truncate" title={c.name}>
                {c.name}
              </span>
              <span className="text-[10px] text-muted-foreground">{c.priority}</span>
            </label>
          )
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Selected tests run together in parallel. Order doesn't matter — failures don't affect siblings.
      </p>
    </>
  )
}

// ── Workflow steps (ordered, drag-reorderable, with labels) ─────────

interface WorkflowStepEditorProps {
  orderedIds: string[]
  casesById: Map<string, PlanTestCase>
  stepLabels: Record<string, string>
  onStepLabelChange: (id: string, label: string) => void
  onRemove: (id: string) => void
  sensors: ReturnType<typeof useSensors>
  onDragEnd: (event: DragEndEvent) => void
  pickerOpen: boolean
  onTogglePicker: () => void
  candidates: PlanTestCase[]
  pickerSearch: string
  onPickerSearchChange: (v: string) => void
  onAdd: (id: string) => void
}

function WorkflowStepEditor({
  orderedIds,
  casesById,
  stepLabels,
  onStepLabelChange,
  onRemove,
  sensors,
  onDragEnd,
  pickerOpen,
  onTogglePicker,
  candidates,
  pickerSearch,
  onPickerSearchChange,
  onAdd,
}: WorkflowStepEditorProps) {
  return (
    <div className="mt-2 space-y-2">
      {orderedIds.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No tests in this workflow yet — add some via "+ Add Tests" below.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-1">
              {orderedIds.map((id, idx) => {
                const c = casesById.get(id)
                return (
                  <WorkflowStepRow
                    key={id}
                    id={id}
                    position={idx + 1}
                    testName={c?.name ?? `${id.slice(0, 8)}… (missing)`}
                    label={stepLabels[id] ?? ''}
                    onLabelChange={(next) => onStepLabelChange(id, next)}
                    onRemove={() => onRemove(id)}
                  />
                )
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* + Add Tests inline picker (mockup's dashed-border button) */}
      <button
        type="button"
        onClick={onTogglePicker}
        className="text-xs text-primary border border-dashed border-primary rounded-md px-3 py-1.5 hover:bg-primary/5"
      >
        {pickerOpen ? '× Close picker' : '+ Add Tests'}
      </button>

      {pickerOpen && (
        <div className="border rounded-md p-2 space-y-2 bg-muted/20">
          <SearchInput
            value={pickerSearch}
            onChange={onPickerSearchChange}
            placeholder="Search tests to add..."
          />
          <div className="max-h-48 overflow-y-auto divide-y border rounded bg-background">
            {candidates.length === 0 ? (
              <p className="text-xs text-muted-foreground italic p-3">
                {pickerSearch
                  ? 'No tests match this search.'
                  : 'Every test in this variant is already in the workflow.'}
              </p>
            ) : (
              candidates.slice(0, 50).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onAdd(c.id)}
                  className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-center gap-2"
                >
                  <Plus className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="text-sm flex-1 truncate" title={c.name}>
                    {c.name}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{c.priority}</span>
                </button>
              ))
            )}
            {candidates.length > 50 && (
              <p className="text-[10px] text-muted-foreground italic p-2 text-center">
                +{candidates.length - 50} more — refine search to narrow
              </p>
            )}
          </div>
        </div>
      )}

      <div className="text-xs px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-900 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-200">
        <span className="font-semibold">Note:</span> The order you set above is for reporting clarity. The run report's workflow card displays tests in this sequence so reviewers can see the business story end-to-end.
      </div>
    </div>
  )
}

function WorkflowStepRow({
  id,
  position,
  testName,
  label,
  onLabelChange,
  onRemove,
}: {
  id: string
  position: number
  testName: string
  label: string
  onLabelChange: (next: string) => void
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-2 py-1.5 rounded border bg-background"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="touch-none text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span
        className="flex items-center justify-center h-5 w-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold shrink-0"
        aria-label={`Step ${position}`}
      >
        {position}
      </span>
      <span className="flex-1 text-sm truncate px-2 py-1 rounded bg-muted/50" title={testName}>
        {testName}
      </span>
      <span className="text-[11px] text-muted-foreground">Label</span>
      <Input
        value={label}
        onChange={(e) => onLabelChange(e.target.value)}
        placeholder="step label"
        aria-label={`Step label for ${testName}`}
        className="h-7 w-32 text-xs"
      />
      <button
        type="button"
        onClick={onRemove}
        className="text-muted-foreground hover:text-destructive"
        aria-label={`Remove ${testName} from workflow`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
